import { queryOptions } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';
import type {
  GetCataloguesFatorH200,
  GetCataloguesTests200,
  GetForms200,
} from '@eureca/recruiter-api-sdk/types';

const STALE_TIME_MS = 5 * 60 * 1000;

export class CataloguesFetchError extends Error {
  readonly name = 'CataloguesFetchError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

/**
 * Catalogue queries powering the job-flow stage editor's three pickers
 * (Formulário / Teste / Fator H).
 *
 * - **Forms** is now backed by the real `forms` module (`GET /forms`); the
 *   stub catalogue endpoint is no longer used here. We project the full
 *   FormRow list into the `{ id, name }` envelope the picker expects.
 * - **Tests** still hits the catalogue STUB on the BFF (`GET /catalogues/tests`)
 *   — that domain hasn't been modeled yet.
 * - **Fator H** hits the real adapter on Core (`GET /catalogues/fator-h`),
 *   which resolves the api_key per company from `api_keys` and calls the
 *   Fator H API. Returns `{ items: [] }` silently when the company has no
 *   key configured. `companySlug` is required — the query is disabled until
 *   the job is loaded and `companySlug` is known.
 */
export const formsCatalogueQueryKey = ['recruiter', 'forms', 'catalogue-projection'] as const;
export const testsCatalogueQueryKey = ['recruiter', 'catalogues', 'tests'] as const;
export const fatorHCatalogueQueryKey = ['recruiter', 'catalogues', 'fator-h'] as const;
export const kolabInstancesQueryKey = ['recruiter', 'catalogues', 'kolab-instances'] as const;

/** EU-764 — catálogo de testes resolvido para UMA vaga. */
export const eligibleTestsQueryKey = (jobId: string) =>
  ['recruiter', 'jobs', jobId, 'eligible-tests'] as const;

/** Projection of `GetForms200` into the picker's `{ items: { id, name }[] }` envelope. */
export interface CatalogueProjection {
  items: { id: string; name: string }[];
}

export const formsCatalogueQueryOptions = queryOptions({
  queryKey: formsCatalogueQueryKey,
  queryFn: async (): Promise<CatalogueProjection> => {
    // Pull a generous page so the picker is rarely paginated; if a tenant
    // ever crosses 100 forms the job-flow editor needs a typeahead and
    // this becomes a search-driven query instead of a static list.
    const res = await fetchClient<GetForms200>({
      method: 'GET',
      url: '/forms',
      params: { page: 1, pageSize: 100, sort: 'name', dir: 'asc' },
    });
    if (res.error) {
      throw new CataloguesFetchError(
        `Failed to load forms catalogue (status ${res.error.status})`,
        res.error.status
      );
    }
    // Shared forms (companySlug === null) get a "(compartilhado)" suffix
    // in the picker label so the consultor can tell them apart from
    // company-specific forms when both are listed side-by-side.
    return {
      items: res.data.items.map((f) => ({
        id: f.id,
        name: f.companySlug === null ? `${f.name} (compartilhado)` : f.name,
      })),
    };
  },
  staleTime: STALE_TIME_MS,
});

export const testsCatalogueQueryOptions = queryOptions({
  queryKey: testsCatalogueQueryKey,
  queryFn: async (): Promise<GetCataloguesTests200> => {
    const res = await fetchClient<GetCataloguesTests200>({
      method: 'GET',
      url: '/catalogues/tests',
    });
    if (res.error) {
      throw new CataloguesFetchError(
        `Failed to load tests catalogue (status ${res.error.status})`,
        res.error.status
      );
    }
    return res.data;
  },
  staleTime: STALE_TIME_MS,
});

export interface EligibleTestEntry {
  id: string;
  name: string;
  /** `false` trava o reenvio em "Não"; `null` deixa o consultor escolher. */
  canResubmit: false | null;
  eligible: boolean;
  ineligibleReason: 'apprentice_job' | null;
}

/**
 * EU-764 — testes que ESTA vaga pode usar.
 *
 * Substitui `testsCatalogueQueryOptions` no editor de etapas. A diferença não
 * é de dados, é de pergunta: o catálogo global responde "quais instrumentos
 * existem", este responde "quais deles se aplicam a esta vaga".
 *
 * A lista vem completa — o inelegível chega marcado, não removido. Quem
 * decide é o Core: o SPA nunca filtra nem infere elegibilidade (RN-03).
 */
export const eligibleTestsQueryOptions = (jobId: string) =>
  queryOptions({
    queryKey: eligibleTestsQueryKey(jobId),
    queryFn: async (): Promise<{ items: EligibleTestEntry[] }> => {
      const res = await fetchClient<{ items: EligibleTestEntry[] }>({
        method: 'GET',
        url: `/jobs/${jobId}/eligible-tests`,
        params: { sort: 'name' },
      });
      if (res.error) {
        throw new CataloguesFetchError(
          `Failed to load eligible tests (status ${res.error.status})`,
          res.error.status
        );
      }
      return res.data;
    },
    staleTime: STALE_TIME_MS,
  });

/**
 * Factory that creates `queryOptions` for the Fator H catalogue, keyed by
 * `companySlug`. The query is disabled when `companySlug` is empty/undefined
 * (i.e. before the job has loaded).
 *
 * The BFF forwards `companySlug` to Core, which resolves the per-company
 * api_key and calls the Fator H API. When the company has no key configured,
 * Core returns `{ items: [] }` — the picker renders empty.
 */
/** Envelope de `GET /catalogues/kolab-instances` (EU-539). */
export interface KolabInstancesResponse {
  instances: { id: string; name: string }[];
}

/**
 * Instâncias Kolab para o select da etapa de teste externo (EU-539).
 * Token da Kolab é global por ambiente (resolvido no Core) — sem key por
 * empresa. `{ instances: [] }` é estado vazio legítimo (cliente sem testes);
 * erro de API (502/503) propaga para a SPA distinguir de vazio.
 */
export const kolabInstancesQueryOptions = queryOptions({
  queryKey: kolabInstancesQueryKey,
  queryFn: async (): Promise<KolabInstancesResponse> => {
    const res = await fetchClient<KolabInstancesResponse>({
      method: 'GET',
      url: '/catalogues/kolab-instances',
    });
    if (res.error) {
      throw new CataloguesFetchError(
        `Failed to load Kolab instances (status ${res.error.status})`,
        res.error.status
      );
    }
    return res.data;
  },
  staleTime: STALE_TIME_MS,
});

export function fatorHCatalogueQueryOptions(companySlug: string) {
  return queryOptions({
    queryKey: [...fatorHCatalogueQueryKey, companySlug] as const,
    enabled: companySlug.length > 0,
    queryFn: async (): Promise<GetCataloguesFatorH200> => {
      const res = await fetchClient<GetCataloguesFatorH200>({
        method: 'GET',
        url: '/catalogues/fator-h',
        params: { companySlug },
      });
      if (res.error) {
        throw new CataloguesFetchError(
          `Failed to load Fator H catalogue (status ${res.error.status})`,
          res.error.status
        );
      }
      return res.data;
    },
    staleTime: STALE_TIME_MS,
  });
}
